import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api/api";
import SideNavLayout from "../../customsidenav/sidenavlayout";
import "./newdash.css";
import { isCoverageNote } from "../cards/visitflow";
import { getPracticeId, patientPhotoUrl } from "../../config/practice";

// New main-dashboard design (mockup port, 2026-09-16). Live data from the
// same endpoint the classic dashboard uses. Route: /dashboard-new.
// Known API gaps (stepper is heuristic until these land in the dashboard
// payload): checkin, checkout, visit_status, tracking.

const practiseid = getPracticeId();

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

const isoDate = (d) => {
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${m}-${day}`;
};
const navDate = (d) => {
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${day}-${m}-${d.getFullYear()}`;
};
const prettyDate = (d) => `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()} ${d.getFullYear()}`;

const fmtDob = (dob) => {
    if (!dob) return "";
    const s = String(dob).slice(0, 10);
    const [y, m, day] = s.split("-");
    if (!y || !m || !day) return s;
    return `${MONTH_NAMES[parseInt(m, 10) - 1] || m} ${parseInt(day, 10)}, ${y}`;
};

const classify = (r) => {
    const vc = String(r.visit_confirmation || "").toLowerCase();
    if (vc.includes("tele")) return "telehealth";
    if (vc.includes("no show") || vc.includes("no-show") || vc.includes("noshow") || vc.includes("cancel")) return "no-show";
    return "active";
};

// Heuristic until checkin/checkout ship in the dashboard payload.
const stepState = (r) => {
    const checkedIn = Number(r.token) > 0 || Boolean(r.room_no) || Boolean(r.signintime);
    const withProvider = checkedIn && (Number(r.room_status) > 0 || Number(r.lov) > 0);
    return { checkedIn, withProvider, checkedOut: false };
};

// The coverage note is not a task: not listed or counted (as on the cards).
const visibleOrders = (r) =>
    Array.isArray(r.orders) ? r.orders.filter((o) => !o.is_hide && !isCoverageNote(o)) : [];

const CheckSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
);
const CardSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
);
const DocSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4.8 3v6a5 5 0 0 0 10 0V3" /><path d="M9.8 14v3a5 5 0 0 0 10 0v-1" /><circle cx="19.8" cy="13.5" r="2.2" /></svg>
);
const CalSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
);
const InfoSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
);
const FlaskSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6" /><path d="M10 3v6.2L4.8 18a2 2 0 0 0 1.8 3h10.8a2 2 0 0 0 1.8-3L14 9.2V3" /><line x1="7.5" y1="15" x2="16.5" y2="15" /></svg>
);

// The photo flag is only maintained by the prod cron (dev copies have '' for
// every row), so always try the public photo store and let onError fall back
// to initials — works identically in prod and dev.
const photoUrl = (row) =>
    patientPhotoUrl(row.patientid);

const Avatar = ({ row, name, className }) => {
    const [broken, setBroken] = useState(false);
    const url = photoUrl(row);
    if (url && !broken) {
        return <img className={className} alt={name} src={url} onError={() => setBroken(true)} />;
    }
    const initials = String(name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
    return (
        <div className={className} style={{ display: "flex", alignItems: "center", justifyContent: "center", background: "#e2e8f0", color: "#475569", fontWeight: 700 }}>
            {initials}
        </div>
    );
};

const Stepper = ({ row, provider }) => {
    const st = stepState(row);
    const step = (done, current, label, icon) => (
        <div className={`grid-step${done ? " done" : current ? " current" : " pending"}${label === "Checkout" ? " checkout" : ""}`}>
            <div className="grid-step-circle">{icon}</div>
            <div className="grid-step-label">{label}</div>
        </div>
    );
    return (
        <div className="grid-stepper">
            {step(st.checkedIn, !st.checkedIn, "Check-in", <CheckSvg />)}
            <div className={`grid-step-line ${st.checkedIn ? "done-line" : "pending-line"}`} />
            {step(st.withProvider, st.checkedIn && !st.withProvider, provider || "Provider", <CheckSvg />)}
            <div className={`grid-step-line ${st.withProvider ? "done-line" : "pending-line"}`} />
            {step(st.checkedOut, st.withProvider, "Checkout", <CardSvg />)}
        </div>
    );
};

const GridCard = ({ row, expanded, onToggle }) => {
    const orders = visibleOrders(row);
    const provider = row.linked_provider || "";
    const name = `${row.firstname || ""} ${row.lastname || ""}`.trim();
    const money = Number(row.copay) > 0 ? `$${row.copay}` : null;
    const followUp = String(row.purpose || "").toUpperCase().includes("FOLLOW");
    return (
        <article className={`grid-pcard${expanded ? " expanded" : ""}`} data-status={classify(row)}>
            <div className="grid-patient-top">
                <div className="grid-avatar-wrap">
                    <Avatar className="grid-avatar" row={row} name={name} />
                    {money && <span className="grid-money">{money}</span>}
                </div>
                <div className="grid-patient-info">
                    <div className="grid-name" title={name}>{name}</div>
                    <div className="grid-id">#{row.patientid}</div>
                    <div className="grid-ins" title={row.insurancename}>{row.insurancename || "—"}</div>
                    <div className="grid-dob">DOB: {fmtDob(row.dob)}</div>
                </div>
                {(row.room_no || Number(row.token) > 0) && (
                    <div className="grid-room">{row.room_no || `T${row.token}`}</div>
                )}
            </div>
            <div className="grid-appt-row">
                <span className="grid-time">{row.start_time || ""}</span>
                {provider && <span className="grid-doc"><DocSvg />{provider}</span>}
                {followUp && <button className="grid-follow" type="button">Follow Up</button>}
            </div>
            <Stepper row={row} provider={provider} />
            <div className="grid-orders-row">
                <span>Orders/Tasks</span>
                <span className="grid-spacer" />
                <span className="grid-badge">{orders.length}</span>
                <button
                    className="grid-chevron"
                    type="button"
                    title="Toggle order details"
                    aria-expanded={expanded}
                    onClick={onToggle}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
            </div>
            {expanded && (
                <div className="grid-order-panel">
                    <div className="grid-order-list">
                        {orders.length === 0 && (
                            <div className="grid-order-item"><span className="grid-order-name">No orders yet</span></div>
                        )}
                        {orders.map((o) => (
                            <div className="grid-order-item" key={o.id}>
                                <span className="grid-order-kind">{String(o.ordertype || "").toUpperCase() || "ORDER"}</span>
                                <span className="grid-order-name" title={o.ordername}>{o.ordername}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {row.notes && (
                <div className="grid-note-box">
                    <div className="grid-note-title">Notes</div>
                    <div className="grid-note-line"><InfoSvg /><span>{row.notes}</span></div>
                </div>
            )}
        </article>
    );
};

const ListCard = ({ row }) => {
    const orders = visibleOrders(row);
    const provider = row.linked_provider || "";
    const name = `${row.firstname || ""} ${row.lastname || ""}`.trim();
    const followUp = String(row.purpose || "").toUpperCase().includes("FOLLOW");
    return (
        <section className="pcard" data-status={classify(row)}>
            <div className="left">
                <div className="identity">
                    <Avatar className="pphoto" row={row} name={name} />
                    <div className="pinfo">
                        <div className="name-line">
                            <span className="pname">{name}</span>
                            <span className="pid">#{row.patientid}</span>
                        </div>
                        <div className="pmeta">DOB: {fmtDob(row.dob)}</div>
                        <div className="ins-line">
                            <span className="ins" title={row.insurancename}>{row.insurancename || "—"}</span>
                            {Number(row.copay) > 0 && <span className="money-chip">${row.copay}</span>}
                        </div>
                    </div>
                    <div className="tag-col">
                        {row.rpm_status === "Y" && (
                            <span className="tag rpm">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 12 7.5 12 10.5 5.5 13.5 18.5 16.5 12 21 12" /></svg>
                                RPM</span>
                        )}
                        {Boolean(row.fcmtoken) && (
                            <span className="tag app">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6.5" y="2.5" width="11" height="19" rx="2.5" /><line x1="10.5" y1="18" x2="13.5" y2="18" /></svg>
                                APP</span>
                        )}
                    </div>
                </div>
                {row.notes && (
                    <div className="notes">
                        <div className="notes-h">NOTES</div>
                        <div className="note-line">
                            <InfoSvg />
                            <span className="note-rest">{row.notes}</span>
                        </div>
                    </div>
                )}
            </div>
            <div className="right">
                <div className="sched-head">
                    <span className="time-chip">{row.start_time || ""}</span>
                    {followUp && <button className="followup-chip" type="button">Follow Up</button>}
                    {provider && <span className="doc-chip"><DocSvg />{provider}</span>}
                </div>
                <div className="orders-h">
                    <span className="t">Orders/Tasks</span>
                    <span className="orders-badge">{orders.length}</span>
                </div>
                <div className="orders-scroll">
                    {orders.map((o) => (
                        <div className="order-item" key={o.id}>
                            <span className="oicon flask"><FlaskSvg /></span>
                            <span className="oname" title={o.ordername}>{o.ordername}</span>
                        </div>
                    ))}
                    {orders.length === 0 && <div className="order-item"><span className="oname">No orders yet</span></div>}
                </div>
            </div>
        </section>
    );
};

const NewDashboard = () => {
    const [date, setDate] = useState(() => new Date());
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("list");
    const [tab, setTab] = useState("active");
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState(() => new Set());

    const fetchDay = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.post(`/v1/${practiseid}/appointments/dashboard`, {
                appointmentdate: isoDate(date),
                filterarr: [],
                start: 0,
                limit: 300,
            });
            const data = res?.data?.data ?? res?.data ?? [];
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("new dashboard fetch failed", e);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [date]);

    useEffect(() => {
        fetchDay();
    }, [fetchDay]);

    const counts = useMemo(() => {
        const c = { active: 0, "no-show": 0, telehealth: 0, "checked-out": 0 };
        rows.forEach((r) => { c[classify(r)] += 1; });
        return c;
    }, [rows]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return rows.filter((r) => {
            if (classify(r) !== tab && !(tab === "checked-out")) return false;
            if (tab === "checked-out") return false; // needs checkout field in the dashboard API
            if (!needle) return true;
            const hay = `${r.firstname} ${r.lastname} ${r.patientid} ${r.insurancename} ${r.linked_provider}`.toLowerCase();
            return hay.includes(needle);
        });
    }, [rows, tab, q]);

    const shiftDay = (delta) => {
        setDate((d) => {
            const next = new Date(d);
            next.setDate(next.getDate() + delta);
            return next;
        });
    };

    const toggleCard = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const isToday = isoDate(date) === isoDate(new Date());

    return (
        <SideNavLayout>
        <div className={`ndz${view === "grid" ? " grid-mode" : ""}`}>
                <main className="main">
                    <div className="greet-row">
                        <div className="greet">
                            <div className="date"><CalSvg />{prettyDate(date)}</div>
                        </div>
                        <div className="greet-right">
                            <button className="refresh-btn" title="Refresh" type="button" onClick={fetchDay}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>
                            </button>
                            <div className="search">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                                <input type="text" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <div className="filter-row">
                        <div className="tabs" aria-label="Queue filters">
                            {[
                                ["active", "Active"],
                                ["no-show", "No-Show"],
                                ["telehealth", "Telehealth"],
                                ["checked-out", "Checked Out"],
                            ].map(([key, label]) => (
                                <button
                                    key={key}
                                    className="tab"
                                    type="button"
                                    aria-pressed={tab === key}
                                    onClick={() => setTab(key)}
                                >
                                    {label} <span className="tab-badge">{counts[key]}</span>
                                </button>
                            ))}
                        </div>
                        <div className="filter-right">
                            <div className="view-toggle">
                                <button className={`js-list-view${view === "list" ? " active" : ""}`} title="List view" aria-pressed={view === "list"} type="button" onClick={() => setView("list")}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></svg>
                                </button>
                                <button className={`js-grid-view${view === "grid" ? " active" : ""}`} title="Grid view" aria-pressed={view === "grid"} type="button" onClick={() => setView("grid")}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="6.5" height="6.5" rx="1" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1" /><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1" /></svg>
                                </button>
                            </div>
                            <div className="date-nav">
                                <button className="chev" title="Previous day" type="button" onClick={() => shiftDay(-1)}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                                </button>
                                <div className="date-center" onClick={() => setDate(new Date())} role="button" tabIndex={0} title="Jump to today">
                                    <div className="lbls">
                                        <div className="today">{isToday ? "Today" : DAY_NAMES[date.getDay()].slice(0, 3)}</div>
                                        <div className="dt">{navDate(date)}</div>
                                    </div>
                                    <CalSvg />
                                </div>
                                <button className="chev" title="Next day" type="button" onClick={() => shiftDay(1)}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    {loading && <div style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>Loading…</div>}
                    {!loading && filtered.length === 0 && (
                        <div style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}>
                            {tab === "checked-out"
                                ? "Checkout tracking needs the checkout field in the dashboard API — coming with the wiring pass."
                                : "No patients in this view."}
                        </div>
                    )}

                    {!loading && view === "grid" && (
                        <section className="queue-grid" aria-label="Patient card grid">
                            {filtered.map((r) => (
                                <GridCard
                                    key={r.appointmentid}
                                    row={r}
                                    expanded={expanded.has(r.appointmentid)}
                                    onToggle={() => toggleCard(r.appointmentid)}
                                />
                            ))}
                        </section>
                    )}
                    {!loading && view === "list" && filtered.map((r) => <ListCard key={r.appointmentid} row={r} />)}
                </main>
        </div>
        </SideNavLayout>
    );
};

export default NewDashboard;
